import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tabs, Tab, Typography, Fade, Paper, Stack } from '@mui/material';
import { cropClasses, fadeTimingMs, baseNewAndDetailClasses, roomDetailClasses, errFeedbackFn } from '../../../utility';
import QualityPlanTimeline from './QualityPlanTimeline';
import APIService from '../../../../api/apiService';
import { transformQualityTimelineData } from '../../utils/QualityTimelineTransformer';

export default function _QualityPlansTab(props) {
  const { values, currDarkThemeClass, handleErrorWithFeedback } = props;
  const [selectedBatchIndex, setSelectedBatchIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rawApiData, setRawApiData] = useState(null);
  const [timeLineData, setTimeLineData] = useState(null);
  const [timelineTotalDays, setTimelineTotalDays] = useState(365);
  const [subphases, setSubphases] = useState([]);

  const fetchBatch = async (batchId) => {
    setIsLoading(true);
    try {
      const response = await APIService.fetchInstance(`v2/cropplan/qualityplan/?batch_id=${batchId}`)
      if (response.status === 200) {
        setRawApiData(response.data || null);

        // Calculate total days from subphases
        let totalDays = 365; // Default fallback
        if (response.data?.batch_details?.subphases_details?.length > 0) {
          const subphasesData = response.data.batch_details.subphases_details;
          setSubphases(subphasesData);

          // Find the maximum end_day across all subphases
          totalDays = Math.max(...subphasesData.map(sp => sp.end_day || 0));
        } else {
          setSubphases([]);
        }

        setTimelineTotalDays(totalDays);

        // Transform the API data to D3 format
        const transformed = transformQualityTimelineData(response.data, totalDays);
        setTimeLineData(transformed);
      }

    }
    catch (error) {
      console.error('Error fetching quality timeline:', error);
      if (typeof handleErrorWithFeedback === "function") {
        handleErrorWithFeedback(errFeedbackFn({}))(error);
      }
    } finally {
      setIsLoading(false);
    }
  }

  const batches = useMemo(() => {
    if (!values?.strains) return [];

    const phaseplans = values?.phaseplans || [];

    return values.strains.flatMap(strain =>
      (strain.batches || []).map(batch => {
        // Find matching phaseplan by phaseplan_id
        const matchingPhasePlan = phaseplans.find(pp => pp.id === batch.phaseplan_id);

        return {
          ...batch,
          strainName: strain.strain_name,
          // frtl: batch?.frtl,
          totalDays: matchingPhasePlan?.total_days || 0,
          // phasePlanName: matchingPhasePlan?.name || 'Unknown',

        };
      })
    );
  }, [values?.strains, values?.phaseplans]);
  const currentBatch = batches[selectedBatchIndex];
  useEffect(() => {
    if (currentBatch?.id) {
      fetchBatch(currentBatch?.id)
    }
  }, [currentBatch?.id])


  const handleTabChange = (event, newValue) => {
    setSelectedBatchIndex(newValue);
  };



  const rTabProps = {
    className: `${baseNewAndDetailClasses?.tab} ${currDarkThemeClass}`,
  };

  const rTabsProps = {
    className: `${baseNewAndDetailClasses?.tabs} ${currDarkThemeClass} roomDetailTabsContent`,
    onChange: handleTabChange,
    value: selectedBatchIndex,
  };

  return (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box className={`customScroll ${cropClasses?.box} ${currDarkThemeClass}`} sx={{ p: 2 }}
      >
        {batches.length === 0 ? (
          <Typography variant="h6" sx={{ textAlign: 'center', mt: 4 }}>
            No batches available for this crop plan.
          </Typography>
        ) : (
          <>
            {/* Standard Tabs for Batches */}
            <Tabs {...rTabsProps} variant="scrollable" scrollButtons={false}>
              {batches.map((batch, index) => (
                <Tab
                  key={index}
                  label={batch.name || `Batch ${index + 1}` + `FRTL: ${batch.frtl}`}
                  {...rTabProps}
                />
              ))}
            </Tabs>

            {/* Batch Info & Quality Plan Grid */}
            {/* Quality Plan Timeline with D3 */}
            {currentBatch && (
              <Box sx={{ mt: 2 }}>
                <QualityPlanTimeline
                  batch={currentBatch}
                  currDarkThemeClass={currDarkThemeClass}
                  timeLineData={timeLineData}
                  isLoading={isLoading}
                  totalDays={timelineTotalDays}
                  subphases={subphases}
                  targetSubphaseId={rawApiData?.target_subphase_id || rawApiData?.batch_details?.target_subphase_id}
                  targetSubphaseDate={rawApiData?.target_subphase_date || rawApiData?.batch_details?.target_subphase_date}
                  actualStartDate={rawApiData?.actual_start_date || rawApiData?.batch_details?.actual_start_date || rawApiData?.start_date || rawApiData?.batch_details?.start_date}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Fade>
  );
}
